import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Scale, Building2, Landmark, ArrowRight, Route, ShieldCheck, Landmark as GovernanceIcon, Users } from "lucide-react";
import { useOutletContext } from "react-router-dom";
import { getPosts } from "@/api";
import { useSeo, ORG_SCHEMA } from "@/seo";
import { Reveal, SectionHead, Eyebrow } from "@/components/Reveal";
import Counter from "@/components/Counter";
import CorridorMap from "@/components/CorridorMap";
import { CalendlyCta } from "@/components/SiteChrome";

const HERO_IMG = "https://images.unsplash.com/photo-1651467606797-e1c660cf3fda?crop=entropy&cs=srgb&fm=jpg&q=80&w=1800";

const SERVICES = [
  {
    icon: Scale,
    title: "Transfer Pricing",
    summary: "Price, document and defend related-party transactions at arm's length, in the UAE and across your markets, especially India.",
    to: "/services/international-tax-transfer-pricing",
    testId: "service-card-tax",
  },
  {
    icon: Building2,
    title: "India Back Office",
    summary: "Trained Indian CAs for UAE Corporate Tax, VAT, audit support and bookkeeping, white-label, under your brand and review.",
    to: "/services/gcc-india-back-office",
    testId: "service-card-gcc",
  },
  {
    icon: Landmark,
    title: "Family Office",
    summary: "A durable home for your family's wealth across India and the Gulf, structure, succession, governance and administration.",
    to: "/services/family-office",
    testId: "service-card-family-office",
  },
];

const WHY = [
  { icon: Route, title: "India UAE corridor expertise", body: "One team that works both jurisdictions daily, tax, FEMA, structures and repatriation." },
  { icon: ShieldCheck, title: "Trusted leadership", body: "Seasoned professionals who have led complex cross-border work, delivered with personal attention." },
  { icon: GovernanceIcon, title: "Tax-aware operating models", body: "Transfer pricing and PE risk designed into your structure, not discovered in an audit." },
  { icon: Users, title: "One point of contact", body: "Tax, operations and wealth under one roof. One conversation, one accountable team." },
];

const PROCESS = [
  { step: "Create", body: "Set the base. Structure, registrations and the right legal and tax foundation for your UAE India position." },
  { step: "Manage", body: "Steady the core. Compliance, documentation, reporting and controls that hold up under scrutiny." },
  { step: "Accelerate", body: "Scale with focus. Capability centres, wealth structures and growth across the corridor." },
];

export default function Home() {
  const { settings } = useOutletContext() || {};
  const [posts, setPosts] = useState([]);
  useSeo({
    title: "TruVerse Middle East, Transfer Pricing, India Back Office & Family Office, Dubai",
    description:
      "Transfer pricing, India back office and family office advisory for UAE businesses, delivered by one trusted cross-border team across Dubai and India.",
    schema: ORG_SCHEMA,
  });

  useEffect(() => {
    getPosts().then((p) => setPosts(p.slice(0, 3))).catch(() => {});
  }, []);

  const counters = settings?.counters || [];

  return (
    <div data-testid="home-page">
      {/* Hero */}
      <section className="relative overflow-hidden bg-ink" data-testid="hero-section">
        <img src={HERO_IMG} alt="Dubai Downtown skyline at dusk" className="absolute inset-0 h-full w-full object-cover" loading="eager" />
        <div className="absolute inset-0 bg-[hsl(231_47%8%/0.28)]" aria-hidden="true" />
        <div className="container-site relative grid items-center gap-12 py-24 lg:grid-cols-2 lg:py-36">
          <div>
            <Reveal>
              <h1 className="mt-2 text-5xl font-bold leading-[1.05] tracking-tight text-ink-fg md:text-6xl lg:text-7xl">
                Your bridge between the UAE, India and the world.
              </h1>
              <p className="mt-6 max-w-xl text-base leading-relaxed text-ink-muted md:text-lg">
                Transfer pricing, India back office and family office advisory, delivered by one trusted cross-border team.
              </p>
              <p className="mt-4 text-sm font-medium uppercase tracking-[0.2em] text-[#9FB6F2]">
                Trusted. One India UAE team.
              </p>
              <div className="mt-10 flex flex-wrap gap-4">
                <CalendlyCta testId="hero-book-consultation-btn" />
                <Link to="/services/international-tax-transfer-pricing" data-testid="hero-explore-services-btn">
                  <span className="group inline-flex items-center rounded-sm border border-ink px-7 py-[18px] text-sm font-semibold text-ink-fg transition-colors duration-200 hover:border-accent hover:text-accent">
                    Explore services
                    <ArrowRight size={16} className="ml-2 transition-transform duration-200 group-hover:translate-x-0.5" />
                  </span>
                </Link>
              </div>
            </Reveal>
          </div>
          <Reveal delay={0.2} className="hidden self-start lg:block">
            <CorridorMap className="h-auto w-full text-ink-fg" />
          </Reveal>
        </div>
      </section>

      {/* Trust bar */}
      <section className="border-b border-border bg-background" data-testid="trust-bar">
        <div className="container-site grid grid-cols-2 gap-10 py-14 lg:grid-cols-4">
          {counters.map((c, i) => (
            <Reveal key={c.id} delay={i * 0.08}>
              <p className="text-4xl font-bold tracking-tight text-foreground md:text-5xl" data-testid={`counter-${c.id}`}>
                <Counter value={c.value} suffix={c.suffix} testId={`counter-value-${c.id}`} />
              </p>
              <p className="mt-2 text-xs font-medium uppercase tracking-[0.2em] text-muted-foreground">{c.label}</p>
              {c.note && <p className="mt-1 text-[11px] italic text-muted-foreground/70">{c.note}</p>}
            </Reveal>
          ))}
        </div>
      </section>

      {/* Services */}
      <section className="py-24" data-testid="services-section">
        <div className="container-site">
          <Reveal>
            <SectionHead
              eyebrow="What we do"
              title="Three practices. One corridor."
              intro="Each engagement is led across Dubai and Chennai by one accountable team."
            />
          </Reveal>
          <div className="mt-14 grid gap-8 md:grid-cols-3">
            {SERVICES.map((s, i) => (
              <Reveal key={s.title} delay={i * 0.1}>
                <Link
                  to={s.to}
                  data-testid={s.testId}
                  className="group flex h-full flex-col border border-border bg-card p-8 transition-all duration-300 hover:-translate-y-1 hover:border-accent"
                >
                  <s.icon size={28} className="text-accent" strokeWidth={1.5} />
                  <h3 className="mt-6 text-2xl font-bold tracking-tight text-foreground">{s.title}</h3>
                  <p className="mt-3 flex-1 text-sm leading-relaxed text-muted-foreground">{s.summary}</p>
                  <span className="mt-6 inline-flex items-center text-xs font-semibold uppercase tracking-[0.2em] text-accent">
                    Learn more <ArrowRight size={14} className="ml-2 transition-transform duration-200 group-hover:translate-x-1" />
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Why TruVerse in Dubai */}
      <section className="bg-secondary/60 py-24" data-testid="why-section">
        <div className="container-site">
          <Reveal>
            <SectionHead eyebrow="Why TruVerse in Dubai" title="Depth where the corridor matters." />
          </Reveal>
          <div className="mt-14 grid gap-px border border-border bg-border sm:grid-cols-2 lg:grid-cols-4">
            {WHY.map((w, i) => (
              <Reveal key={w.title} delay={i * 0.08} className="bg-card">
                <div className="flex h-full flex-col p-8" data-testid={`why-tile-${i + 1}`}>
                  <w.icon size={24} className="text-accent" strokeWidth={1.5} />
                  <h3 className="mt-5 text-lg font-bold text-foreground">{w.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{w.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Create Manage Accelerate */}
      <section className="py-24" data-testid="framework-section">
        <div className="container-site">
          <Reveal>
            <SectionHead
              eyebrow="How we work"
              title="Create. Manage. Accelerate."
              intro="The TruVerse framework, adapted for businesses and families building between the UAE and India."
            />
          </Reveal>
          <div className="mt-14 grid gap-8 md:grid-cols-3">
            {PROCESS.map((p, i) => (
              <Reveal key={p.step} delay={i * 0.12}>
                <div className="relative border-t-2 border-accent pt-8" data-testid={`framework-step-${p.step.toLowerCase()}`}>
                  <span className="text-6xl font-bold text-accent/20">{String(i + 1).padStart(2, "0")}</span>
                  <h3 className="mt-3 text-2xl font-bold uppercase tracking-wide text-foreground">{p.step}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{p.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Founder spotlight */}
      <section className="bg-ink py-24" data-testid="founder-section">
        <div className="container-site grid items-center gap-12 lg:grid-cols-5">
          <Reveal className="lg:col-span-2">
            <div className="border border-ink p-2">
              <img
                src="/founder.webp"
                alt="Vikram Jain, Founder of TruVerse"
                className="aspect-[4/4.4] w-full object-cover object-top"
                loading="lazy"
                data-testid="founder-portrait"
              />
            </div>
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-3">
            <Eyebrow>Founder</Eyebrow>
            <h2 className="mt-4 text-4xl font-bold tracking-tight text-ink-fg md:text-5xl">Vikram Jain</h2>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-ink-muted md:text-lg">
              17+ years advising multinationals, global business leaders, expatriates, HNIs and Indian entrepreneurs on tax,
              regulatory, business and wealth matters. Formerly Associate Director at B S R & Co. LLP (KPMG India).
            </p>
            <a
              href="https://www.linkedin.com/in/vikram-jain-6085b515/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="founder-linkedin-link"
              className="mt-6 inline-flex items-center text-xs font-semibold uppercase tracking-[0.2em] text-accent transition-colors duration-200 hover:text-ink-fg"
            >
              Connect on LinkedIn <ArrowRight size={14} className="ml-2" />
            </a>
          </Reveal>
        </div>
      </section>

      {/* Insights preview */}
      <section className="py-24" data-testid="insights-preview-section">
        <div className="container-site">
          <Reveal>
            <div className="flex items-end justify-between gap-6">
              <SectionHead eyebrow="Insights" title="From the corridor desk." />
              <Link to="/insights" data-testid="insights-view-all-link" className="hidden shrink-0 items-center text-xs font-semibold uppercase tracking-[0.2em] text-accent md:inline-flex">
                All insights <ArrowRight size={14} className="ml-2" />
              </Link>
            </div>
          </Reveal>
          <div className="mt-14 grid gap-8 md:grid-cols-3">
            {posts.map((p, i) => (
              <Reveal key={p.doc_id} delay={i * 0.08}>
                <Link to={`/insights/${p.slug}`} data-testid={`insight-card-${p.slug}`} className="group flex h-full flex-col border border-border bg-card transition-all duration-300 hover:-translate-y-1 hover:border-accent">
                  <div className="p-8">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-accent">{p.category}</p>
                    <h3 className="mt-4 text-xl font-bold leading-snug text-foreground">{p.title}</h3>
                    <p className="mt-3 flex-1 text-sm leading-relaxed text-muted-foreground">{p.excerpt}</p>
                    <p className="mt-6 text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
                      {new Date(p.created_at).toLocaleDateString("en-AE", { day: "numeric", month: "long", year: "numeric" })}
                    </p>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="border-t border-border bg-secondary/60 py-24" data-testid="final-cta-section">
        <div className="container-site flex flex-col items-start justify-between gap-8 lg:flex-row lg:items-center">
          <Reveal>
            <h2 className="text-4xl font-bold tracking-tight text-foreground md:text-5xl">Make the next move inevitable.</h2>
            <p className="mt-4 max-w-xl text-base text-muted-foreground md:text-lg">
              A thirty-minute conversation with a senior advisor. No obligation, no jargon.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <CalendlyCta testId="final-cta-book-btn" />
          </Reveal>
        </div>
      </section>
    </div>
  );
}
